import React, { useState, useEffect } from 'react';
import { getTickets, getProductsForFilter } from '../../services/supportApi';
import './TicketList.css';

function TicketList({ onSelectTicket, refreshTrigger }) {
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    product: '',
    submitted_by: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [refreshTrigger]);

  const fetchProducts = async () => {
    try {
      const response = await getProductsForFilter();
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTickets();
      setTickets(response.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-open';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      case 'closed':
        return 'status-closed';
      default:
        return '';
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({ status: '', product: '', submitted_by: '' });
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'closed', label: 'Closed' }
  ];

  // Derive unique submitters from tickets
  const submitters = [...new Set(tickets.map(t => t.submitted_by))].sort();

  // Apply filters client-side since getTickets doesn't support query params
  const filteredTickets = tickets.filter(ticket => {
    if (filters.status && ticket.status !== filters.status) return false;
    if (filters.product && String(ticket.product) !== filters.product) return false;
    if (filters.submitted_by && ticket.submitted_by !== filters.submitted_by) return false;
    return true;
  });

  // Get client name from first ticket (all tickets belong to same client)
  const clientName = tickets.length > 0 ? tickets[0].client_name : null;

  if (loading) {
    return (
      <div className="ticketList">
        <div className="ticketList-loading">Loading tickets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticketList">
        <div className="ticketList-error">
          {error}
          <button onClick={fetchTickets} className="ticketList-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="ticketList">
        <div className="ticketList-empty">
          No support tickets yet. Submit one above to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="ticketList">
      <h2>{clientName ? `Tickets for ${clientName}` : 'Your Tickets'}</h2>

      <div className="ticketList-filters">
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <select
          name="product"
          value={filters.product}
          onChange={handleFilterChange}
        >
          <option value="">All Products</option>
          {products.map(product => (
            <option key={product.id} value={product.id}>{product.name}</option>
          ))}
        </select>

        <select
          name="submitted_by"
          value={filters.submitted_by}
          onChange={handleFilterChange}
        >
          <option value="">All Submitters</option>
          {submitters.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        {(filters.status || filters.product || filters.submitted_by) && (
          <button className="ticketList-clearFilters" onClick={clearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {filteredTickets.length === 0 ? (
        <div className="ticketList-empty">No tickets match the selected filters.</div>
      ) : (
        <div className="ticketList-table-wrapper">
          <table className="ticketList-table">
            <thead>
              <tr>
                <th className="ticketList-unread-col"></th>
                <th>ID</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Product</th>
                <th>Submitted By</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map(ticket => (
                <tr key={ticket.id} onClick={() => onSelectTicket(ticket.id)}>
                  <td className="ticketList-unread-col">
                    {ticket.has_unread && <span className="ticketList-unread-dot" />}
                  </td>
                  <td className="ticketList-id">{ticket.client_code ? `${ticket.client_code}-${ticket.id}` : `#${ticket.id}`}</td>
                  <td className="ticketList-subject">{ticket.subject}</td>
                  <td>{ticket.form_type_name}</td>
                  <td>{ticket.product_name || '—'}</td>
                  <td>{ticket.submitted_by}</td>
                  <td>
                    <span className={`ticketList-status ${getStatusClass(ticket.status)}`}>
                      {ticket.status_display}
                    </span>
                  </td>
                  <td>{formatDate(ticket.created_at)}</td>
                  <td>{formatDate(ticket.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="ticketList-count">
        {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default TicketList;
